import { NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMe } from '../hooks/useMe';
import { useTaskCounts } from '../hooks/useTaskCounts';
import { ThemeToggle } from './ThemeToggle';
import { TrashIcon } from './TrashIcon';

interface Props {
  counts?: Partial<Record<string, number>>;
  onNavigate?: () => void;
}

/**
 * Sidebar entries are derived from `/me.groups` (ADR 0009 §7):
 *   - Combined (inbox)
 *   - Personal — the caller's own personal partition (`/group/personal`)
 *   - One link per company group the caller is a member of
 *   - Private, Completed, Trash (unchanged)
 *   - Settings — super-admin only (Phase 7 surface)
 *
 * If the user is in 4+ groups, company labels get truncated at 14 chars via
 * CSS. Loading state renders a subtle skeleton so the drawer doesn't jump.
 */
export function ScopeSidebar({ counts: propCounts, onNavigate }: Props) {
  const { user, signOut } = useAuth();
  const { me } = useMe();
  const { counts: taskCounts } = useTaskCounts();

  // Combined = sum of all visible groups' open tasks.
  const combinedTotal = Object.values(taskCounts).reduce((a, b) => a + b, 0);

  const companyGroups = me?.groups.filter((g) => g.kind === 'company') ?? [];
  const personalTotal = (me?.groups ?? [])
    .filter((g) => g.kind === 'personal')
    .reduce((acc, g) => acc + (taskCounts[g.id] ?? 0), 0);
  const totalGroupEntries = 1 /* personal */ + companyGroups.length;
  const truncate = totalGroupEntries >= 4;

  return (
    <nav className="scope-sidebar" aria-label="Groups">
      <div className="scope-brand">
        <h2>Tasks</h2>
      </div>

      {!me ? (
        <ul className="scope-list scope-list-skeleton" aria-busy="true">
          <li><span className="scope-skeleton-row" /></li>
          <li><span className="scope-skeleton-row" /></li>
          <li><span className="scope-skeleton-row" /></li>
        </ul>
      ) : (
        <ul className={`scope-list${truncate ? ' scope-list-truncate' : ''}`}>
          <li>
            <NavLink to="/" end className={({ isActive }) => (isActive ? 'active' : '')} onClick={onNavigate}>
              <span className="scope-swatch" style={{ backgroundColor: '#f59e0b' }} />
              <span className="scope-label">Combined</span>
              {combinedTotal > 0 ? (
                <span className="scope-count">{combinedTotal}</span>
              ) : propCounts?.inbox ? (
                <span className="scope-count">{propCounts.inbox}</span>
              ) : null}
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/group/personal"
              className={({ isActive }) => (isActive ? 'active' : '')}
              onClick={onNavigate}
            >
              <span className="scope-swatch" style={{ backgroundColor: me.color }} />
              <span className="scope-label">Personal</span>
              {personalTotal > 0 ? (
                <span className="scope-count">{personalTotal}</span>
              ) : null}
            </NavLink>
          </li>
          {companyGroups.map((g) => {
            const count = taskCounts[g.id] ?? propCounts?.[g.id] ?? 0;
            return (
              <li key={g.id}>
                <NavLink
                  to={`/group/${encodeURIComponent(g.id)}`}
                  className={({ isActive }) => (isActive ? 'active' : '')}
                  onClick={onNavigate}
                  title={g.displayName}
                >
                  <span className="scope-swatch" style={{ backgroundColor: g.color }} />
                  <span className="scope-label">{g.displayName}</span>
                  {count > 0 ? <span className="scope-count">{count}</span> : null}
                </NavLink>
              </li>
            );
          })}
        </ul>
      )}

      <div className="scope-sidebar-footer">
        <NavLink
          to="/private"
          className={({ isActive }) => `private-link ${isActive ? 'active' : ''}`}
          title="Tasks only you can see"
          onClick={onNavigate}
        >
          <span className="private-icon" aria-hidden="true">🔒</span>
          <span className="scope-label">Private</span>
        </NavLink>
        <NavLink
          to="/completed"
          className={({ isActive }) => `completed-link ${isActive ? 'active' : ''}`}
          onClick={onNavigate}
          title="Recently completed tasks (last 30 days)"
        >
          <span className="private-icon" aria-hidden="true">✓</span>
          <span className="scope-label">Completed</span>
        </NavLink>
        <NavLink
          to="/trash"
          className={({ isActive }) => `trash-link ${isActive ? 'active' : ''}`}
          onClick={onNavigate}
        >
          <TrashIcon />
          <span className="scope-label">Trash</span>
        </NavLink>
        <NavLink
          to="/templates"
          className={({ isActive }) => `templates-link ${isActive ? 'active' : ''}`}
          onClick={onNavigate}
          title="Recurring task templates"
        >
          <span className="private-icon" aria-hidden="true">▤</span>
          <span className="scope-label">Templates</span>
        </NavLink>
        {me?.isSuperAdmin && (
          <NavLink
            to="/settings"
            className={({ isActive }) => `settings-link ${isActive ? 'active' : ''}`}
            onClick={onNavigate}
            title="Manage users and companies"
          >
            <span className="private-icon" aria-hidden="true">⚙</span>
            <span className="scope-label">Settings</span>
          </NavLink>
        )}
        <ThemeToggle />
        {user && (
          <button className="signout-btn" onClick={signOut} title={user.email}>
            Sign out
          </button>
        )}
      </div>
    </nav>
  );
}
